<template>
  <div class="border border-gray-300 p-3 rounded-xl z-10 relative h-52" :class="slotCssClass()">
    <DeviceSetupAlert
      v-if="!deviceLabel"
      :uiNotificationType="uiNotificationType"
      :uiNotificationTitle="uiNotificationTitle"
      :uiNotification="uiNotification"
    ></DeviceSetupAlert>

    <div class="absolute bottom-2 left-0 w-full h-12 mb-2 pl-4 pr-4">
      <div class="flex items-center justify-between">
        <div class="flex items-center space-x-3">
          <!-- First div -->
          <div
            class="rounded-full w-12 h-12 min-w-12 max-h-12 flex justify-center items-center"
            :class="slotIconBackgroundCssClass()"
          >
            <img
              v-if="deviceTypeIcon"
              :src="'/storage/' + deviceTypeIcon"
              :alt="deviceTypeLabel"
              width="25"
              height="25"
              class=""
            />
          </div>
          <!-- Second div -->
          <div class="">
            <div class="device-type-label-regular font-medium">{{ deviceTypeLabel }}</div>
            <div class="device-label-regular font-medium">
              <span v-if="deviceLabel">{{ deviceLabel }}</span>
              <span v-else>Not Setup</span>
            </div>
          </div>
        </div>
        <!-- Last div -->
        <div class="">
          <AddDeviceIcon
            @click="showSetDeviceModal"
            v-show="!deviceLabel"
            class="cursor-pointer float-right"
          ></AddDeviceIcon>
          <DeleteDeviceIcon
            v-show="deviceLabel"
            @click="showDeleteDevicesModal"
            class="cursor-pointer float-right"
          ></DeleteDeviceIcon>
        </div>
      </div>
    </div>

    <!-- Devices preview -->
    <VideoStreamPreview
      v-if="
        deviceKind === MEDIA_DEVICE_KIND.VIDEO_INPUT &&
        showDevicePreview &&
        savedDeviceConnectedToSlot
      "
      :deviceId="deviceIdInSlot"
    ></VideoStreamPreview>

    <AudioStreamPreview
      v-if="
        deviceKind === MEDIA_DEVICE_KIND.AUDIO_INPUT &&
        showDevicePreview &&
        savedDeviceConnectedToSlot
      "
      :deviceId="deviceIdInSlot"
    ></AudioStreamPreview>
    <!-- / Devices preview -->

    <!-- Device preview controls -->
    <div class="w-full flex justify-end" v-if="deviceLabel && savedDeviceConnectedToSlot">
      <div class="cursor-pointer" v-if="showDevicePreview" @click="stopDevicePreview()">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="48" height="48">
          <circle cx="50" cy="50" r="50" fill="#ff0000" />
          <rect x="30" y="30" width="40" height="40" fill="white" />
        </svg>
      </div>

      <div
        class="cursor-pointer"
        v-if="!showDevicePreview && deviceLabel"
        @click="startDevicePreview()"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="48" height="48">
          <circle cx="50" cy="50" r="50" fill="#0d9488" />
          <polygon points="35,25 35,75 75,50" fill="white" />
        </svg>
      </div>
    </div>
    <!-- / Device preview controls -->
  </div>
</template>
<script>
import { ref, watch, onMounted } from 'vue';
import useEventsBus from '@/Services/EventBus.js';
import useDeviceAndSessionSetupStore from '@/Stores/useDeviceAndSessionSetupStore';
import AddDeviceIcon from '@/Components/Icons/DeviceAndSessionSetup/AddDeviceIcon.vue';
import DeleteDeviceIcon from '@/Components/Icons/DeviceAndSessionSetup/DeleteDeviceIcon.vue';
import DeviceSetupAlert from '@/Components/Alerts/DeviceAndSessionSetup/DeviceSetupAlert.vue';
import VideoStreamPreview from './VideoStreamPreview.vue';
import AudioStreamPreview from './AudioStreamPreview.vue';
import { MEDIA_DEVICE_KIND } from '@/Utils/vice-constants.js';

export default {
  name: 'UseCaseSlot',
  props: {
    useCaseId: {
      type: Number,
      required: true,
    },
    deviceSlotId: {
      type: Number,
      required: true,
    },
    deviceTypeLabel: {
      type: String,
      required: true,
    },
    deviceKind: {
      type: String,
      required: false,
      default: null,
    },
    deviceTypeIcon: {
      type: String,
      required: true,
    },
    uiNotificationType: {
      type: String,
      required: false,
      default: null,
    },
    uiNotificationTitle: {
      type: String,
      required: false,
      default: null,
    },
    uiNotification: {
      type: String,
      required: false,
      default: null,
    },
    storedClinicianDevice: {
      type: Object,
      required: false,
      default: null,
    },
  },
  components: {
    AddDeviceIcon,
    DeleteDeviceIcon,
    DeviceSetupAlert,
    VideoStreamPreview,
    AudioStreamPreview,
  },
  setup(props) {
    const { emit, bus } = useEventsBus();

    const deviceAndSessionSetupStore = useDeviceAndSessionSetupStore();

    const deviceKind = ref('');
    const deviceLabel = ref('');
    const deviceIdInSlot = ref('');
    const showDevicePreview = ref(false);
    const savedDeviceConnectedToSlot = ref(false);

    onMounted(() => {
      // Set device data to slot if device was saved in this slot
      deviceLabel.value = props.storedClinicianDevice?.label;
      deviceKind.value = props.storedClinicianDevice?.kind;

      checkIfSavedDeviceConnected();
    });

    // watching device connecting or disconnecting (with ability to check "on the fly")
    watch(
      () => deviceAndSessionSetupStore.getConnectedDevices(props.deviceKind),
      (devices) => {
        checkIfSavedDeviceConnected(devices);
      },
    );

    const checkIfSavedDeviceConnected = (devices) => {
      let connectedDevicesList;

      if (devices) {
        connectedDevicesList = devices;
      } else {
        connectedDevicesList = deviceAndSessionSetupStore.getConnectedDevices(props.deviceKind);
      }

      // We have to rely on the device label to check if the device is connected due to the
      // fact that the different USB interfaces give different deviceID for same device
      const savedDevice = connectedDevicesList.find(
        (obj) => obj.label === deviceLabel.value && obj.kind === deviceKind.value,
      );

      savedDeviceConnectedToSlot.value = !!savedDevice;

      // Set ID of device that was saved in this slot
      deviceIdInSlot.value = savedDevice?.deviceId;
    };

    // Listen to event when device added to slot
    watch(
      () => bus.value.get('deviceAddedToDeviceTypeSlot'),
      (deviceArgs) => {
        const args = deviceArgs[0];
        if (args.deviceSlotId === props.deviceSlotId && props.deviceKind) {
          deviceKind.value = args.deviceKind;
          deviceLabel.value = args.deviceLabel;
          deviceIdInSlot.value = args.deviceId;
          savedDeviceConnectedToSlot.value = true;

          startDevicePreview();
        }
      },
    );

    // Listen to event when device deleted from slot
    watch(
      () => bus.value.get('deviceDeletedFromDeviceTypeSlot'),
      (deviceArgs) => {
        const args = deviceArgs[0];

        if (args.deviceSlotId === props.deviceSlotId) {
          stopDevicePreview();

          deviceLabel.value = null;
          deviceIdInSlot.value = null;
          savedDeviceConnectedToSlot.value = false;
        }
      },
    );

    const startDevicePreview = () => {
      showDevicePreview.value = true;
    };

    const stopDevicePreview = () => {
      showDevicePreview.value = false;
    };

    const showSetDeviceModal = () => {
      // emit event when clinician trying to add device
      emit('showSetDeviceModal', {
        useCaseId: props.useCaseId,
        deviceSlotId: props.deviceSlotId,
        deviceKind: props.deviceKind,
        deviceTypeLabel: props.deviceTypeLabel,
      });
    };

    const showDeleteDevicesModal = () => {
      // emit event when clinician trying to delete device
      emit('showDeleteDevicesModal', {
        deviceSlotId: props.deviceSlotId,
      });
    };

    const slotCssClass = () => {
      if (deviceLabel.value && !savedDeviceConnectedToSlot.value) {
        return 'slot-background-device-not-connected';
      }

      return 'slot-background-default';
    };

    const slotIconBackgroundCssClass = () =>
      deviceLabel.value && savedDeviceConnectedToSlot.value
        ? 'slot-icon-background-active'
        : 'slot-icon-background-default';

    return {
      showSetDeviceModal,
      showDeleteDevicesModal,
      deviceLabel,
      deviceIdInSlot,
      showDevicePreview,
      savedDeviceConnectedToSlot,
      slotIconBackgroundCssClass,
      slotCssClass,
      MEDIA_DEVICE_KIND,
      startDevicePreview,
      stopDevicePreview,
    };
  },
};
</script>
<style scoped>
.slot-background-default,
.slot-background-device-not-connected {
  background-position: center;
  background-repeat: no-repeat;
  background-size: initial;
}

.slot-background-default {
  background-image: url('/image/icons/device-slot.svg');
}

.slot-background-device-not-connected {
  background-image: url('/image/icons/device-not-connected.svg');
}

.slot-icon-background-default {
  background-color: #c4c4c4;
}

.slot-icon-background-active {
  background-color: #0d9488;
}

.device-type-label-regular,
.device-type-label-preview {
  color: #881337;
  font-size: 16px;
}

.device-label-regular,
.device-label-preview {
  font-size: 12px;
  color: #4b4b4b;
}
</style>
